import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { type ReactNode, type Ref, useState } from 'react';
import { Input, type InputProps } from '../components/input.js';
import { uiText } from '../i18n.js';
import { cn } from '../lib/utils.js';
import { FieldWrapper } from './field-wrapper.js';
import type { FieldValueChange } from './types.js';

type ControlledInputType = 'text' | 'email' | 'password' | 'url' | 'search' | 'tel';

export interface ControlledInputFieldProps
  extends Omit<InputProps, 'id' | 'name' | 'onChange' | 'type' | 'value'> {
  readonly name: string;
  readonly value: string;
  readonly onValueChange: FieldValueChange<string>;
  readonly label?: ReactNode | undefined;
  readonly labelClassName?: string | undefined;
  readonly description?: string | undefined;
  readonly error?: string | undefined;
  /** Aviso não-bloqueante exibido quando `error` está ausente. */
  readonly warning?: string | undefined;
  readonly type?: ControlledInputType | undefined;
  readonly icon?: ReactNode | undefined;
  readonly className?: string | undefined;
  readonly inputClassName?: string | undefined;
  readonly inputRef?: Ref<HTMLInputElement> | undefined;
  readonly required?: boolean | undefined;
  /** Exibe o botão interno de mostrar/ocultar senha para `type="password"`. */
  readonly showPasswordToggle?: boolean | undefined;
  readonly showPasswordLabel?: string | undefined;
  readonly hidePasswordLabel?: string | undefined;
  /** Reserva altura do slot de erro mesmo quando ausente. */
  readonly reserveErrorSlot?: boolean | undefined;
}

export function ControlledInputField({
  name,
  value,
  onValueChange,
  label,
  labelClassName,
  description,
  error,
  warning,
  type = 'text',
  icon,
  className,
  inputClassName,
  inputRef,
  required,
  showPasswordToggle = true,
  showPasswordLabel,
  hidePasswordLabel,
  reserveErrorSlot,
  disabled,
  ...inputProps
}: ControlledInputFieldProps) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const hasPasswordToggle = isPassword && showPasswordToggle;
  const resolvedType = hasPasswordToggle && showPassword ? 'text' : type;
  const hasError = error !== undefined && error !== '';

  return (
    <FieldWrapper
      name={name}
      label={label}
      labelClassName={labelClassName}
      description={description}
      error={error}
      warning={warning}
      required={required}
      className={className}
      reserveErrorSlot={reserveErrorSlot}
    >
      <span className="relative flex items-center">
        {icon !== undefined && (
          <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            {icon}
          </span>
        )}
        <Input
          ref={inputRef}
          id={name}
          name={name}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          type={resolvedType}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${name}-error` : undefined}
          className={cn(
            'h-11',
            icon !== undefined && 'pl-9',
            hasPasswordToggle && 'pr-10',
            hasError && 'border-destructive focus-visible:ring-destructive/30',
            inputClassName,
          )}
          {...inputProps}
        />
        {hasPasswordToggle && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((current) => !current)}
            className="text-muted-foreground hover:text-foreground absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
            aria-label={
              showPassword
                ? (hidePasswordLabel ?? uiText('common.hidePassword'))
                : (showPasswordLabel ?? uiText('common.showPassword'))
            }
            aria-pressed={showPassword}
            disabled={disabled}
          >
            {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
          </button>
        )}
      </span>
    </FieldWrapper>
  );
}
